/**
 * Purple Color System
 * Based on UX Design Tokens (ERP/Audit/theme_1.png)
 *
 * Primary brand color: Purple (#7257F8 for light theme, #9254DE for dark theme)
 * Replaces default blue (#1890ff) throughout the application
 */

/**
 * Purple color palette for light mode
 * 10 shades from lightest (purple1) to darkest (purple10)
 * Source: ERP/Audit/theme_1.png
 */
export const purpleColors = {
  // Light Mode - Purple Shades
  purple1: '#F5F0FF',   // Lightest - Background colors
  purple2: '#F4F0FF',   // Very light - Hover backgrounds
  purple3: '#DFD4FF',   // Light - Borders
  purple4: '#BEABFF',   // Medium-light - Border hover
  purple5: '#9B82FF',   // Medium - Hover states
  purple6: '#7257F8',   // BASE PRIMARY - Main brand color
  purple7: '#523FD1',   // Medium-dark - Active states
  purple8: '#9B82FF',   // Repeat of purple5
  purple9: '#7257F8',   // Repeat of purple6
  purple10: '#523FD1',  // Repeat of purple7
};

/**
 * Purple color palette for dark mode
 * Adjusted for better visibility on dark backgrounds
 * Note: In dark mode, we need brighter purples for better contrast
 */
export const purpleColorsDark = {
  purple1: '#1F1A2E',   // Dark mode darkest background (darker for better contrast)
  purple2: '#2D2850',   // Dark mode very dark background
  purple3: '#391E57',   // Dark mode dark background
  purple4: '#531DAB',   // Dark mode medium-dark
  purple5: '#722ED1',   // Dark mode medium (same as light purple6 for consistency)
  purple6: '#9254DE',   // BASE PRIMARY for dark mode (brighter for visibility!)
  purple7: '#B37FEB',   // Dark mode bright
  purple8: '#D3ADF7',   // Dark mode very bright
  purple9: '#EFDBFF',   // Dark mode brightest
  purple10: '#F9F0FF',  // Dark mode lightest
};

/**
 * Status colors (keep existing success, warning, error)
 * These remain unchanged from Ant Design defaults
 */
export const statusColors = {
  success: '#52c41a',
  warning: '#faad14',
  error: '#ff4d4f',
};

/**
 * Neutral colors for text and backgrounds
 */
export const neutralColors = {
  // Light mode
  light: {
    textPrimary: 'rgba(0, 0, 0, 0.88)',
    textSecondary: 'rgba(0, 0, 0, 0.65)',
    textTertiary: 'rgba(0, 0, 0, 0.45)',
    textQuaternary: 'rgba(0, 0, 0, 0.25)',
    bgContainer: '#ffffff',
    bgElevated: '#ffffff',
    bgLayout: '#f5f5f5',
    bgSpotlight: '#fafafa',
    border: '#d9d9d9',
    borderSecondary: '#f0f0f0',
  },
  // Dark mode
  dark: {
    textPrimary: 'rgba(255, 255, 255, 0.85)',
    textSecondary: 'rgba(255, 255, 255, 0.65)',
    textTertiary: 'rgba(255, 255, 255, 0.45)',
    textQuaternary: 'rgba(255, 255, 255, 0.25)',
    bgContainer: '#141414',
    bgElevated: '#1f1f1f',
    bgLayout: '#000000',
    bgSpotlight: '#262626',
    border: '#434343',
    borderSecondary: '#303030',
  },
};

/**
 * Search Highlight Colors
 * Used for highlighting search matches in tables
 */
export const highlightColors = {
  light: 'rgba(114, 87, 248, 0.25)',    // Purple at 25% opacity for light mode
  dark: 'rgba(155, 130, 255, 0.45)',     // Brighter purple at 45% opacity for dark mode
};

/**
 * Elevation Neutrals — hairline ring colors for the shadow-border elevation set
 * (consumed by `elevationTokens` in tokens.ts + `useElevation()`).
 *
 * `ring` is the WCAG 1.4.11 (Non-text Contrast) compliance element: a 1px hairline
 * that must INDEPENDENTLY clear 3:1 against `colorBgContainer`/`colorBgElevated` so
 * a surface boundary is perceivable without relying on the accompanying shadow.
 * NOTE: the existing `colorBorder` (#d9d9d9 light) is only ~1.41:1 vs #ffffff and
 * therefore FAILS 1.4.11 — these are deliberately-chosen NEW tokens, not that border.
 * Measured ratios (standard WCAG relative-luminance formula):
 *   light ring #8c8c8c vs #ffffff .......... 3.36:1 ✓
 *   dark  ring #7a7a7a vs #141414 (container) 4.29:1 ✓ / vs #1f1f1f (elevated) 3.84:1 ✓
 *
 * `ringSubtle` is decorative-only (1.4.11 decoration exemption) — a faint separator
 * for edges where an adjacent shadow already conveys the boundary. It is NOT gated.
 */
export const elevationNeutrals = {
  light: {
    ring: '#8c8c8c',        // WCAG-compliant hairline (3.36:1 vs #ffffff)
    ringSubtle: '#e8e8e8',  // decorative separator only — exempt from 1.4.11
  },
  dark: {
    ring: '#7a7a7a',        // WCAG-compliant hairline (3.84:1 vs #1f1f1f)
    ringSubtle: '#3a3a3a',  // decorative separator only — exempt from 1.4.11
  },
};

/**
 * Export all colors as a single object
 */
export const colors = {
  purple: purpleColors,
  purpleDark: purpleColorsDark,
  status: statusColors,
  neutral: neutralColors,
  highlight: highlightColors,
};

export default colors;
